import {
    Component,
    OnInit,
    AfterViewInit,
    Input,
    Output,
    EventEmitter,
    ElementRef,
    ViewChild,
    OnChanges,
    SimpleChanges,
    OnDestroy
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ButtonDirective} from "primeng/button";
import { TooltipModule } from "primeng/tooltip";
import {IconPathPipe} from "@app/utils/pipes/icon-path-pipe";

declare var ol: any;

@Component({
    selector: 'app-openlayers-map',
    standalone: true,
    imports: [CommonModule, ButtonDirective, IconPathPipe, TooltipModule],
    templateUrl: './openlayers-map.component.html',
    styleUrls: ['./openlayers-map.component.scss'],
})
export class OpenlayersMapComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    @ViewChild('mapContainer', {static: true}) mapContainer!: ElementRef;

    @Input() height = '100%';
    @Input() center: [number?, number?] = [69.2401, 41.2995]; 
    @Input() zoom = 13;
    @Input() drawingMode = false;
    @Input() satellite: boolean = false;
    @Input() polygonJson:string | undefined


    @Output() coordinatesChange = new EventEmitter<{ lat: number; lng: number }[]>();
    @Output() markerClick = new EventEmitter<{ lat: number; lng: number }>();
    @Output() mapClick = new EventEmitter<{ lat: number; lng: number }>();
    @Output() mapMove = new EventEmitter<any>();
    @Output() polygonJsonChange = new EventEmitter<any>();

    private map: any;
    private vectorSource: any;
    private vectorLayer: any;
    private centerMarker: any;
    public satelliteLayer: any;
    private dynamicPolygonsSource: any;
    private dynamicPolygonsLayer: any;
    private polygonCountLayer: any;
    private maxVisiblePolygons = 100; 
    mapLoaded = false;

    private drawInteraction: any;

    private polygonPoints: any[] = [];

    private isDrawing = false;
    private ndiLayer: any;
    private modifyInteraction: any;




    ngOnInit() {
        if (!document.querySelector('link[href*="ol.css"]')) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = 'https://cdn.jsdelivr.net/gh/openlayers/openlayers.github.io@master/en/v6.15.1/css/ol.css';
            document.head.appendChild(link);
        }

        if (typeof ol === 'undefined') {
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/gh/openlayers/openlayers.github.io@master/en/v6.15.1/build/ol.js';
            script.onload = () => {
                this.initializeMap();
            };
            script.onerror = () => {
                console.error('Failed to load OpenLayers');
            };
            document.body.appendChild(script);
        }
    }

    ngAfterViewInit() {
        if (typeof ol !== 'undefined') {
            setTimeout(() => this.initializeMap(), 100);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['center'] && this.map && !changes['center'].firstChange) {
            const coords = changes['center'].currentValue as [number, number];
            this.updateCenterMarker(coords[0], coords[1]);
        }
    }

    private initializeMap() {
        if (!this.mapContainer || this.map) return;

        try {

            if (typeof ol === 'undefined') {
                console.error('OpenLayers library not loaded');
                return;
            }

            this.vectorSource = new ol.source.Vector();
            this.vectorLayer = new ol.layer.Vector({
                source: this.vectorSource,
                style: new ol.style.Style({
                    fill: new ol.style.Fill({
                        color: 'rgba(119, 255, 15, 0.34)'
                    }),
                    stroke: new ol.style.Stroke({
                        color: '#ff4b4b',
                        width: 2
                    }),
                    image: new ol.style.Circle({
                        radius: 5,
                        fill: new ol.style.Fill({
                            color: '#ff0000'
                        }),
                        stroke: new ol.style.Stroke({
                            color: '#ffffff',
                            width: 2
                        })
                    })
                })
            });
            const osmLayer = new ol.layer.Tile({
                source: new ol.source.OSM()
            });

            this.map = new ol.Map({
                target: this.mapContainer.nativeElement,
                layers: [osmLayer, this.vectorLayer],
                view: new ol.View({
                    center: ol.proj.fromLonLat(this.center),
                    zoom: this.zoom
                })
            });

            this.map.on('click', (evt: any) => {
                const coordinate = evt.coordinate;
                const lonLat = ol.proj.toLonLat(coordinate);

                if (this.drawingMode) {
                    if (!this.isDrawing) {
                        this.startPolygonDrawing();
                        this.isDrawing = true;
                    }

                } else {
                    this.markerClick.emit({lat: lonLat[1], lng: lonLat[0]});
                }
            });

            //@ts-ignore
            this.updateCenterMarker(this.center[1], this.center[0]);

            this.map.on('moveend', () => {
                const bounds = this.getMapBounds();
                const view = this.map.getView();
                const center = ol.proj.toLonLat(view.getCenter());
                this.mapMove.emit({
                    bounds,
                    center: {lat: center[1], lng: center[0]},
                    zoom: view.getZoom()
                });

                this.updatePolygonVisibility();
            });

            this.mapLoaded = true;
        } catch (error) {
            console.error('Error initializing OpenLayers map:', error);
        }

        if (this.satellite) {
            this.addSatelliteLayer();
        }
    }


    public startPolygonDrawing() {
        if (this.drawInteraction) {
            this.map.removeInteraction(this.drawInteraction);
        }

        this.drawInteraction = new ol.interaction.Draw({
            source: this.vectorSource,
            type: 'Polygon'
        });

        this.drawingMode = true;


        this.drawInteraction.on('drawstart', (event: any) => {
            const polygon = event.feature;
            polygon.set('type', 'polygon');


            polygon.getGeometry().on('change', (geomEvt: any) => {
                const coords = geomEvt.target.getCoordinates()[0];


                this.vectorSource.getFeatures()
                    .filter((f: any) => f.get('type') === 'vertex')
                    .forEach((f: any) => this.vectorSource.removeFeature(f));


                coords.forEach((coord: any) => {
                    const pointFeature = new ol.Feature({
                        geometry: new ol.geom.Point(coord),
                        type: 'vertex'
                    });
                    this.vectorSource.addFeature(pointFeature);
                });


                const latlngs = coords.map((c: any) => {
                    const lonLat = ol.proj.toLonLat(c);
                    return {lat: lonLat[1], lng: lonLat[0]};
                });
                this.coordinatesChange.emit(latlngs);
            });
        });

        this.drawInteraction.on('drawend', (event: any) => {
            this.isDrawing = false;
            this.map.removeInteraction(this.drawInteraction);
            this.enableEditPolygon();

            this.polygonJson = `POLYGON ((${event.feature.getGeometry().getCoordinates()[0]
                .map((coord: [number, number]) => `${coord[0].toFixed(2)} ${coord[1].toFixed(2)}`)
                .join(', ')}))`;
            this.polygonJsonChange.emit(this.polygonJson);
        });

        this.map.addInteraction(this.drawInteraction);
    }

    public updateCenterMarker(lon: number, lat: number) {
        const view = this.map.getView();
        view.animate({
            center: ol.proj.fromLonLat([lon, lat]),
            duration: 250
        });

        if (this.map && typeof ol !== 'undefined') {
            if (this.centerMarker) {
                this.vectorSource.removeFeature(this.centerMarker);
            }

            const markerFeature = new ol.Feature({
                geometry: new ol.geom.Point(ol.proj.fromLonLat([lon, lat])),
                type: 'centerMarker'
            });

            markerFeature.setStyle(new ol.style.Style({
                image: new ol.style.Icon({
                    anchor: [0.5, 1],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'fraction',
                    src: 'data:image/svg+xml;base64,' + btoa(`
            <svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.5 0C5.596 0 0 5.596 0 12.5c0 2.902 1.004 5.57 2.68 7.682L12.5 41l9.82-20.818C23.996 18.07 25 15.402 25 12.5 25 5.596 19.404 0 12.5 0z" fill="#ff0000"/>
              <circle cx="12.5" cy="12.5" r="6" fill="white"/>
            </svg>
          `),
                    scale: 1
                })
            }));

            this.vectorSource.addFeature(markerFeature);
            this.centerMarker = markerFeature;
        }
    }

    public clearCenterMarker() {
        if (this.centerMarker && this.vectorSource) {
            this.vectorSource.removeFeature(this.centerMarker);
            this.centerMarker = null;
        }
    }

    public addSatelliteLayer() {
        if (!this.map || !ol) return;

        if (!this.satelliteLayer) {
            this.satelliteLayer = new ol.layer.Tile({
                source: new ol.source.XYZ({
                    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
                    attributions: '© Esri'
                }),
                opacity: 1
            });

            const layers = this.map.getLayers();
            layers.insertAt(1, this.satelliteLayer);
        }
    }

    public removeSatelliteLayer() {
        if (this.map && this.satelliteLayer) {
            this.map.removeLayer(this.satelliteLayer);
            this.satelliteLayer = null;
        }
    }

    public toggleSatelliteLayer() {
        if (this.satelliteLayer) {
            this.removeSatelliteLayer();
        } else {
            this.addSatelliteLayer();
        }
    }

    public setSatelliteOpacity(opacity: number) {
        if (this.satelliteLayer) {
            this.satelliteLayer.setOpacity(opacity / 100);
        }
    }

    public initDynamicPolygonsLayer() {
        if (!this.map || !ol) return;

        if (!this.dynamicPolygonsSource) {
            this.dynamicPolygonsSource = new ol.source.Vector();
            this.dynamicPolygonsLayer = new ol.layer.Vector({
                source: this.dynamicPolygonsSource,
                style: new ol.style.Style({
                    fill: new ol.style.Fill({
                        color: 'rgba(51, 136, 255, 0.3)'
                    }),
                    stroke: new ol.style.Stroke({
                        color: '#3388ff',
                        width: 2
                    })
                })
            });
            this.map.addLayer(this.dynamicPolygonsLayer);
        }
    }

    public addDynamicPolygon(coordinates: number[][], properties: any = {}) {
        if (!this.dynamicPolygonsSource) {
            this.initDynamicPolygonsLayer();
        }

        const olCoords = coordinates.map(coord => ol.proj.fromLonLat([coord[1], coord[0]]));

        const polygon = new ol.Feature({
            geometry: new ol.geom.Polygon([olCoords])
        });

        if (properties.color || properties.fillColor) {
            const style = new ol.style.Style({
                fill: new ol.style.Fill({
                    color: properties.fillColor || properties.color || 'rgba(51, 136, 255, 0.3)'
                }),
                stroke: new ol.style.Stroke({
                    color: properties.color || '#3388ff',
                    width: 2
                })
            });
            polygon.setStyle(style);
        }

        if (properties.popup) {
            polygon.set('popup', properties.popup);
        }

        this.dynamicPolygonsSource.addFeature(polygon);

        this.updatePolygonVisibility();
    }

    public clearDynamicPolygons() {
        if (this.dynamicPolygonsSource) {
            this.dynamicPolygonsSource.clear();
        }

        this.hidePolygonCountOverlay();
    }

    public getDynamicPolygonsCount(): number {
        if (this.dynamicPolygonsSource) {
            return this.dynamicPolygonsSource.getFeatures().length;
        }
        return 0;
    }

    private updatePolygonVisibility() {
        if (!this.dynamicPolygonsLayer || !this.map) return;

        const count = this.getDynamicPolygonsCount();
        const view = this.map.getView();
        const zoom = view.getZoom();

        if (zoom >= 15) {
            this.dynamicPolygonsLayer.setVisible(true);

            this.hidePolygonCountOverlay();
        } else if (count > this.maxVisiblePolygons) {
            this.dynamicPolygonsLayer.setVisible(false);

            this.showPolygonCountOverlay(count);
        } else {
            this.dynamicPolygonsLayer.setVisible(true);
            this.hidePolygonCountOverlay();
        }
    }

    private showPolygonCountOverlay(count: number) {
        if (!this.map || !ol || !this.dynamicPolygonsSource) return;

        this.hidePolygonCountOverlay();

        const allFeatures = this.dynamicPolygonsSource.getFeatures();

        const view = this.map.getView();
        const extent = view.calculateExtent(this.map.getSize());

        const gridSize = 5; 
        const width = extent[2] - extent[0];
        const height = extent[3] - extent[1];
        const cellWidth = width / gridSize;
        const cellHeight = height / gridSize;

        const grid = new Map<string, { count: number; center: number[]; features: any[] }>();

        allFeatures.forEach((feature: any) => {
            const geom = feature.getGeometry();
            if (geom && geom.getType() === 'Polygon') {
                const coords = geom.getCoordinates()[0];
                const center = ol.extent.getCenter(ol.extent.boundingExtent(coords));

                const gridX = Math.floor((center[0] - extent[0]) / cellWidth);
                const gridY = Math.floor((center[1] - extent[1]) / cellHeight);

                if (gridX >= 0 && gridX < gridSize && gridY >= 0 && gridY < gridSize) {
                    const key = `${gridX}_${gridY}`;
                    if (!grid.has(key)) {
                        const cellCenterX = extent[0] + (gridX + 0.5) * cellWidth;
                        const cellCenterY = extent[1] + (gridY + 0.5) * cellHeight;
                        grid.set(key, {count: 0, center: [cellCenterX, cellCenterY], features: []});
                    }
                    grid.get(key)!.count++;
                    grid.get(key)!.features.push(feature);
                }
            }
        });

        const features: any[] = [];
        const polygonsToShow: any[] = [];

        grid.forEach((cell, key) => {
            if (cell.count > 0) {
                if (!this.checkPolygonsOverlap(cell.features) && cell.count < 10) {
                    cell.features.forEach(f => {
                        polygonsToShow.push(f);
                    });
                } else {
                    const feature = new ol.Feature({
                        geometry: new ol.geom.Point(cell.center)
                    });

                    feature.setStyle(new ol.style.Style({
                        text: new ol.style.Text({
                            text: cell.count.toString(),
                            font: 'bold 20px Arial',
                            fill: new ol.style.Fill({color: '#fff'}),
                            stroke: new ol.style.Stroke({color: '#333', width: 4}),
                            offsetY: 0,
                            backgroundFill: new ol.style.Fill({color: 'rgba(255, 107, 107, 0.8)'}),
                            padding: [4, 8, 4, 8]
                        })
                    }));

                    features.push(feature);
                }
            }
        });

        polygonsToShow.forEach(polygon => {
            features.push(polygon);
        });

        const vectorSource = new ol.source.Vector({
            features: features
        });

        this.polygonCountLayer = new ol.layer.Vector({
            source: vectorSource,
            zIndex: 999
        });

        this.dynamicPolygonsLayer.setVisible(false);

        this.map.addLayer(this.polygonCountLayer);
    }

    private hidePolygonCountOverlay() {
        if (this.polygonCountLayer && this.map) {
            this.map.removeLayer(this.polygonCountLayer);
            this.polygonCountLayer = null;
        }
    }

    private checkPolygonsOverlap(features: any[]): boolean {
        if (features.length < 2) return false;

        for (let i = 0; i < features.length - 1; i++) {
            const geom1 = features[i].getGeometry();
            const extent1 = geom1.getExtent();

            for (let j = i + 1; j < features.length; j++) {
                const geom2 = features[j].getGeometry();
                const extent2 = geom2.getExtent();

                if (ol.extent.intersects(extent1, extent2)) {
                    return true;
                }
            }
        }

        return false;
    }



    public getMapBounds(): any {
        if (this.map) {
            const extent = this.map.getView().calculateExtent(this.map.getSize());
            const bottomLeft = ol.proj.toLonLat([extent[0], extent[1]]);
            const topRight = ol.proj.toLonLat([extent[2], extent[3]]);

            return {
                west: bottomLeft[0],
                south: bottomLeft[1],
                east: topRight[0],
                north: topRight[1]
            };
        }
        return null;
    }

    ngOnDestroy() {
        this.stopPolygonDrawing();
        if (this.ndiLayer) {
            this.map.removeLayer(this.ndiLayer);
            this.ndiLayer = null;
        }
        if (this.map) {
            this.map.setTarget(null);
            this.map = null;
        }
    }



    private updatePolygon() {
        if (this.polygonPoints.length < 1) return;

        this.vectorSource.clear();

        this.polygonPoints.forEach(coord => {
            const point = new ol.Feature({
                geometry: new ol.geom.Point(coord)
            });
            this.vectorSource.addFeature(point);
        });

        if (this.polygonPoints.length > 1) {
            const line = new ol.Feature({
                geometry: new ol.geom.LineString(this.polygonPoints)
            });
            this.vectorSource.addFeature(line);
        }

        if (this.polygonPoints.length >= 3) {
            const polygon = new ol.Feature({
                geometry: new ol.geom.Polygon([[...this.polygonPoints]])
            });
            this.vectorSource.addFeature(polygon);
        }
    }

    public stopPolygonDrawing() {
        if (this.drawInteraction) {
            this.map.removeInteraction(this.drawInteraction);
            this.drawInteraction = null;
        }

        if (this.vectorSource) {
            this.vectorSource.clear();
        }

        this.drawingMode = false;
    }

    public zoomIn() {
        if (this.map) {
            const view = this.map.getView();
            const zoom = view.getZoom();
            view.animate({
                zoom: zoom + 1,
                duration: 250
            });
        }
    }

    public zoomOut() {
        if (this.map) {
            const view = this.map.getView();
            const zoom = view.getZoom();
            view.animate({
                zoom: zoom - 1,
                duration: 250
            });
        }
    }


    public centerMap() {
        if (this.map && this.center) {
            const view = this.map.getView();
            view.animate({
                center: ol.proj.fromLonLat(this.center),
                zoom: this.zoom,
                duration: 250
            });
        }
    }

private enableEditPolygon() {
  if (!this.vectorSource) return;

  if (this.modifyInteraction) {
    this.map.removeInteraction(this.modifyInteraction);
  }


  this.modifyInteraction = new ol.interaction.Modify({
    source: this.vectorSource
  });

  this.map.addInteraction(this.modifyInteraction);


  this.modifyInteraction.on('modifyend', (event: any) => {
    const features = event.features.getArray();
    features.forEach((feature: any) => {
      if (feature.getGeometry().getType() === 'Polygon') {
        const coords = feature.getGeometry().getCoordinates()[0];
        const latlngs = coords.map((c: any) => {
          const lonLat = ol.proj.toLonLat(c);
          return {lat: lonLat[1], lng: lonLat[0]};
        });
        this.coordinatesChange.emit(latlngs);

        this.polygonJson = `POLYGON ((${coords
          .map((coord: [number, number]) => `${coord[0].toFixed(2)} ${coord[1].toFixed(2)}`)
          .join(', ')}))`;
        this.polygonJsonChange.emit(this.polygonJson);
      }
    });
  });
}


}
